import React, { useEffect, useState } from "react";
import { Eye, X } from "lucide-react";
import { Badge } from "./TeacherMocksShared";
import {
  difficultyBadgeVariant,
  difficultyLabels,
  questionTypeTag,
  stripHtml,
} from "./teacherMocksHelpers";
import { apiGet } from "../../processes/helpers/api";

const TYPE_BADGE_VARIANT = {
  "type-calc": "type-calc",
  "type-case": "type-case",
  "type-interactive": "type-interactive",
  "type-recurring": "type-recurring",
  "type-no-calc": "outline",
};

function initial(name) {
  return (name || "?").trim()[0]?.toUpperCase() ?? "?";
}

function choiceText(content) {
  return stripHtml(content || "")
    .replace(/\s+/g, " ")
    .trim();
}

function QuestionChoices({ detail, isLoading }) {
  if (isLoading) {
    return (
      <div className="teacher-mocks-view-state-small">
        Carregando alternativas...
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="teacher-mocks-view-state-small">
        Alternativas não disponíveis.
      </div>
    );
  }

  const stems = detail.meta?.questionStems || [];
  const stem = stems[0];
  const choices = stem?.choices?.length ? stem.choices : null;

  if (choices) {
    return (
      <>
        {choices
          .slice()
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map((choice, i) => (
            <div
              key={choice.id || i}
              className="teacher-mocks-view-question-choice"
            >
              <span className="teacher-mocks-view-choice-letter">
                {String.fromCharCode(65 + i)}
              </span>
              <span>{choiceText(choice.content)}</span>
            </div>
          ))}
      </>
    );
  }

  const prompts = detail.meta?.questionPrompts || [];
  const alternatives = prompts[0]?.alternatives || [];

  if (alternatives.length) {
    return (
      <>
        {alternatives.map((alt, i) => (
          <div key={alt.id || i} className="teacher-mocks-view-question-choice">
            <span className="teacher-mocks-view-choice-letter">
              {String.fromCharCode(65 + i)}
            </span>
            <span>{choiceText(alt.content)}</span>
          </div>
        ))}
      </>
    );
  }

  return (
    <div className="teacher-mocks-view-state-small">
      Alternativas não disponíveis.
    </div>
  );
}

export default function ViewMockDrawer({ open, onClose, mock, isLoading }) {
  const [tab, setTab] = useState("questions");
  const [expandedId, setExpandedId] = useState(null);
  const [questionDetails, setQuestionDetails] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [enrolledCount, setEnrolledCount] = useState(null);

  useEffect(() => {
    if (!open) return;

    setTab("questions");
    setExpandedId(null);
    setQuestionDetails({});
    setLoadingId(null);
    setEnrolledCount(null);

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (tab !== "publication" || !mock?.courseId || enrolledCount !== null)
      return;

    let mounted = true;
    apiGet(`/api/v2/manager/users?course_id=${mock.courseId}&per_page=1`)
      .then((res) => {
        if (mounted) setEnrolledCount(res.body?.pagination?.totalItems ?? null);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [tab, mock?.courseId, enrolledCount]);

  if (!open) return null;

  const questions = mock?.questions || [];
  const questionsCount = questions.reduce(
    (sum, q) => sum + (q.stemsCount || 1),
    0,
  );

  const linkStudents = mock?.linkStudents || [];

  async function toggleQuestion(questionId) {
    if (expandedId === questionId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(questionId);
    if (questionDetails[questionId]) return;
    setLoadingId(questionId);
    try {
      const res = await apiGet(`/api/v2/manager/questions/${questionId}`);
      const data = res.body?.data;
      if (data) setQuestionDetails((prev) => ({ ...prev, [questionId]: data }));
    } catch {
      /* ignorado intencionalmente */
    }
    setLoadingId(null);
  }

  function availabilityLabel() {
    if (mock?.availabilityMode === "module_completion")
      return "Ao completar um módulo do curso";
    if (mock?.availabilityMode === "specific")
      return "Alunos específicos selecionados";
    if (enrolledCount === null) return "Todos os alunos do curso";
    return `Todos os ${enrolledCount} alunos do curso`;
  }

  return (
    <div className="teacher-mocks-view-overlay" onMouseDown={onClose}>
      <aside
        className="teacher-mocks-view-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Visualizar simulado"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="teacher-mocks-view-header">
          <div className="teacher-mocks-view-header-info">
            {isLoading && !mock?.name ? (
              <h2 className="teacher-mocks-view-title-placeholder">
                Carregando...
              </h2>
            ) : (
              <h2 className="teacher-mocks-view-title">{mock?.name || "Simulado"}</h2>
            )}
            <p>
              {mock?.courseName || ""}
              {questionsCount > 0 ? ` · ${questionsCount} questões` : ""}
              {" · Somente leitura"}
            </p>
          </div>
          <button
            type="button"
            className="teacher-mocks-view-close"
            aria-label="Fechar"
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <nav className="teacher-mocks-view-tabs" aria-label="Abas do simulado">
          <button
            type="button"
            className={`teacher-mocks-view-tab${tab === "questions" ? " active" : ""}`}
            onClick={() => setTab("questions")}
          >
            Questões
          </button>
          <button
            type="button"
            className={`teacher-mocks-view-tab${tab === "publication" ? " active" : ""}`}
            onClick={() => setTab("publication")}
          >
            Publicação
          </button>
        </nav>

        <div className="teacher-mocks-view-content">
          {tab === "questions" && (
            <div className="teacher-mocks-view-questions-list">
              {isLoading && questions.length === 0 && (
                <div className="teacher-mocks-view-state">
                  Carregando questões...
                </div>
              )}
              {!isLoading && questions.length === 0 && (
                <div className="teacher-mocks-view-state">
                  Nenhuma questão neste simulado.
                </div>
              )}
              {questions.map((question, index) => {
                const isExpanded = expandedId === question.questionId;
                const isLoadingThis = loadingId === question.questionId;
                const detail = questionDetails[question.questionId];
                const diffVariant = difficultyBadgeVariant(
                  question.difficultyLevel,
                );
                const diffLabel = difficultyLabels[question.difficultyLevel];
                const typeTag = questionTypeTag(question);
                const html = question.context || question.firstStemContent || "";
                const qId = `Q-${String(question.questionId).padStart(4, "0")}`;

                return (
                  <div
                    key={question.questionId}
                    className={`teacher-mocks-view-question-row${isExpanded ? " expanded" : ""}`}
                  >
                    <span className="teacher-mocks-view-question-num">
                      {index + 1}
                    </span>
                    <div className="teacher-mocks-view-question-content">
                      <div className="teacher-mocks-view-question-header">
                        <div className="teacher-mocks-view-question-main">
                          <div className="teacher-mocks-view-question-badges">
                            <Badge variant="blue">{qId}</Badge>
                            {diffLabel && (
                              <Badge variant={diffVariant}>{diffLabel}</Badge>
                            )}
                            <Badge
                              variant={
                                TYPE_BADGE_VARIANT[typeTag.className] ||
                                "outline"
                              }
                            >
                              {typeTag.label}
                            </Badge>
                          </div>
                          <div
                            className="teacher-mocks-view-question-text"
                            dangerouslySetInnerHTML={{ __html: html }}
                          />
                        </div>
                        {typeTag.className !== "type-interactive" && (
                          <button
                            type="button"
                            className="teacher-mocks-view-eye"
                            aria-label={
                              isExpanded ? "Recolher questão" : "Expandir questão"
                            }
                            onClick={() => toggleQuestion(question.questionId)}
                          >
                            <Eye
                              size={16}
                              aria-hidden="true"
                              color={isExpanded ? "#0369a1" : undefined}
                            />
                          </button>
                        )}
                      </div>
                      {isExpanded && (
                        <div className="teacher-mocks-view-question-expanded">
                          <QuestionChoices
                            detail={detail}
                            isLoading={isLoadingThis}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "publication" && (
            <div className="teacher-mocks-view-publication">
              <div className="teacher-mocks-view-pub-section">
                <span className="teacher-mocks-view-pub-label">
                  Nome do simulado
                </span>
                <div className="teacher-mocks-view-pub-input-box">
                  <span className="teacher-mocks-view-pub-input-text">
                    {mock?.name || ""}
                  </span>
                </div>
              </div>

              <div className="teacher-mocks-view-pub-section">
                <span className="teacher-mocks-view-pub-label">
                  Disponibilizado para
                </span>
                <div className="teacher-mocks-view-pub-box">
                  <span className="teacher-mocks-view-pub-box-text">
                    {availabilityLabel()}
                  </span>
                </div>
              </div>

              {linkStudents.length > 0 && (
                <div className="teacher-mocks-view-pub-section">
                  <span className="teacher-mocks-view-pub-label">
                    Alunos que entraram pelo link
                  </span>
                  <div className="teacher-mocks-view-pub-box">
                    {linkStudents.map((student, idx) => (
                      <div
                        key={student.id}
                        className={`teacher-mocks-view-link-student${idx > 0 ? " has-gap" : ""}`}
                      >
                        <div
                          className="teacher-mocks-view-avatar"
                          aria-hidden="true"
                        >
                          {initial(student.name)}
                        </div>
                        <span className="teacher-mocks-view-student-name">
                          {student.name}
                        </span>
                        <span className="teacher-mocks-view-student-email">
                          {student.email}
                        </span>
                        <span className="teacher-mocks-view-via-link">
                          (via link)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
